import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
  Box,
  Typography,
  OutlinedInput,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@tanstack/react-query";
import SubscriptionServices from "@/services/main/subscriptionServices";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Button, { BtnType } from "@/components/common/buttons/Button";
import ErrorMessage from "@/components/common/text/ErrorMessage";
import { freeEmailDomains } from "@/constants/businessEmail";
import { useAppDispatch } from "@/lib/hooks";
import { setErrorMess, setSuccessMess } from "@/lib/features/statusSlice";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslations } from "next-intl";

interface FormData {
  fullname: string;
  email: string;
  phone: string;
  message: string;
  captcha?: string;
  type?: number;
}
const phoneRegExp = /(((\+|)84)|0)(3|5|7|8|9)+([0-9]{8})\b/;
interface Props {
  isDisableIcon?: boolean;
  isDisableButton?: boolean;
  type?: number;
  title: string;
}
const PopupNeed = forwardRef(function PopupNeed({
  isDisableIcon,
  isDisableButton = false,
  type,
  title
}: Props, ref) {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [open, setOpen] = React.useState(false);
  const [captchaValue, setcaptchaValue] = useState<string | null>(null)
  const [showCaptcha, setShowCaptcha] = useState<boolean>(false);
  const t = useTranslations("messages");
  const dispatch = useAppDispatch();
  useImperativeHandle(ref, () => ({
    handleOpen: () => setOpen(true),
    handleClose: () => setOpen(false),
  }), []);
  const schema = useMemo(() => {
    return yup.object().shape({
      fullname: yup.string().required(() => t("field_full_name_required")),
      email: yup.string()
        .trim()
        .email(() => t("field_helper_email"))
        .required(() => t("field_email_vali_required"))
        .test("is-business-email", () => t("field_helper_email"), (value) => {
          const domainSplit = value.split("@")[1];
          return !value || !freeEmailDomains.some((domain) => domain === domainSplit);
        }),
      phone: yup.string()
        .trim()
        .nullable()
        .typeError(() => t("field_phone_number_vali_phone"))
        .required(() => t("field_phone_number_vali_required"))
        .test(
          "is-valid-phone",
          () => t("field_phone_number_vali_phone"),
          (value) => !value || phoneRegExp.test(value)
        ),
      message: yup.string().required(() => t("field_helper_message_required")),
      captcha: yup.string().when([], {
        is: () => showCaptcha,
        then: (schema) => schema.required(() => t("field_helper_captcha_required")),
        otherwise: (schema) => schema.optional(),
      }),
    })
  }, [])
  const onChangeCaptCha = (value: any) => {
    setValue("captcha", value);
  }
  const { register, handleSubmit, formState: { errors }, reset, clearErrors, setError, setValue } = useForm<FormData>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      fullname: "",
      email: "",
      phone: "",
      message: "",
      captcha: "",
    },
  });

  const mutationSendNeedHelp = useMutation({
    mutationFn: async (data: FormData) => {
      const result = await SubscriptionServices.createNeedHelp(data);
      return result
    },
    onSuccess: () => {
      setOpen(false);
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      reset();
      dispatch(setSuccessMess(t("helper_mess_success")));
    },
    onError: (error: any) => {
      setShowCaptcha(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      dispatch(setErrorMess(error?.detail || t("helper_mess_error")));
    },
  })

  const handleSubmitNeedHelp = async (data: FormData) => {
    if (!data.captcha) {
      setShowCaptcha(true);
      return;
    }
    clearErrors();
    mutationSendNeedHelp.mutate({
      ...data,
      type: type
    });
  }
  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby={"dialog-title"}
        disableScrollLock
        slotProps={{
          paper: {
            onSubmit: handleSubmit(handleSubmitNeedHelp),
            component: "form",
            sx: {
              width: "100%",
              maxWidth: "480px",
            },
          }
        }}
      >
        <DialogTitle
          id={"dialog-title"}
          className="py-[16px] px-[24px] rounded-tl-[8px] rounded-tr-[8px]   "
        >
          <Box className="flex bg-[#FFFFFF] justify-between items-center ">
            <Box>

              <Typography
                component={"span"}
                className="!font-semibold text-[16px] leading-[24px] tracking-[0] font-montserrat !text-[#1A1D20] "
                translation-key={title}
              >
                {t(title)}
              </Typography>
            </Box>
            <Box className="flex items-center bg-[#F1F3F4] rounded-[50px] p-[4px] cursor-pointer hover:!bg-[#E8E8E8] ">
              <CloseIcon onClick={() => setOpen(false)} />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className="!p-0 rounded-bl-[8px] rounded-br-[8px]">
          <Box className="flex flex-col gap-[24px] px-[24px] pt-[24px] pb-[48px] bg-[#FFFFFF]">
            <Box>
              <Typography
                component={"p"}
                className="font-montserrat font-normal !text-[14px] leading-[24px] tracking-[0]"
                translation-key="common_need_assistance"
              >
                {t("common_need_assistance")}
              </Typography>
            </Box>
            <Box className="flex flex-col gap-[10px]">
              <Box className="flex flex-col gap-[12px]">
                <Box>
                  <Typography
                    component={"span"}
                    className="!font-[Montserrat]  !font-medium !leading-[16px] tracking-[0] text-[#1A1D20] !text-[14px]"
                    translation-key="common_your_name"
                  >
                    {t("common_your_name")}
                  </Typography>
                </Box>
                <Box>
                  <OutlinedInput
                    {...register("fullname")}
                    className={
                      `!py-[8px] !px-[16px] border-[1px] border-solid ${errors.fullname ? "!border-red-500" : "border-[#BDC3C9]"}  bg-[#FFFFFF] max-h-[40px]`
                    }
                    translation-key="field_helper_your_full_name_placeholder"
                    placeholder={t("field_helper_your_full_name_placeholder")}
                    slotProps={{
                      input: {
                        className:
                          "!font-[Montserrat]  !font-normal !text-[14px] leading-[24px] tracking-[0] text-[#1A1D20] !p-0 min-h-[24px]",
                      },
                    }}
                    fullWidth
                  />
                  {errors.fullname && (
                    <ErrorMessage>{errors.fullname.message}</ErrorMessage>
                  )}
                </Box>
              </Box>
              <Box className="flex flex-col gap-[12px]">
                <Box>
                  <Typography
                    component={"span"}
                    className="!font-[Montserrat]  !font-medium !leading-[16px] tracking-[0] text-[#1A1D20] !text-[14px]"
                    translation-key="common_your_email"
                  >
                    {t("field_email_business")}
                  </Typography>
                </Box>
                <Box>
                  <OutlinedInput
                    {...register("email")}
                    className={
                      `!py-[8px] !px-[16px] border-[1px] border-solid  ${errors.email ? "!border-red-500" : "border-[#BDC3C9]"}  bg-[#FFFFFF] max-h-[40px]`
                    }
                    translation-key="field_email_business_placeholder"
                    placeholder={t("field_email_business_placeholder")}
                    slotProps={{
                      input: {
                        className:
                          "!font-[Montserrat]  !font-normal !text-[14px] leading-[24px] tracking-[0] text-[#1A1D20] !p-0 min-h-[24px]",
                      },
                    }}
                    fullWidth
                  />
                  {errors.email && (
                    <ErrorMessage>{errors.email.message}</ErrorMessage>
                  )}
                </Box>
              </Box>
              <Box className="flex flex-col gap-[12px]">
                <Box>
                  <Typography
                    component={"span"}
                    className="!font-[Montserrat]  !font-medium !leading-[16px] tracking-[0] text-[#1A1D20] !text-[14px]"
                    translation-key="common_your_phone_number"
                  >
                    {t("common_your_phone_number")}
                  </Typography>
                </Box>
                <Box>
                  <OutlinedInput
                    {...register("phone")}
                    className={
                      `!py-[8px] !px-[16px] border-[1px] border-solid  ${errors.phone ? "!border-red-500" : "border-[#BDC3C9]"}  bg-[#FFFFFF] max-h-[40px]`
                    }
                    slotProps={{
                      input: {
                        className:
                          "!font-[Montserrat]  !font-normal !text-[14px] leading-[24px] tracking-[0] text-[#1A1D20] !p-0 min-h-[24px]",
                      },
                    }}
                    fullWidth
                    translation-key="common_e_g_84932123331"
                    placeholder={t("common_e_g_84932123331")}
                  />
                  {errors.phone && (
                    <ErrorMessage>{errors.phone.message}</ErrorMessage>
                  )}
                </Box>
              </Box>
              <Box className="flex flex-col gap-[12px]">
                <Box>
                  <Typography
                    component={"span"}
                    className="!font-[Montserrat]  !font-medium !leading-[16px] tracking-[0] text-[#1A1D20] !text-[14px]"
                    translation-key="common_your_message"
                  >
                    {t("common_your_message")}
                  </Typography>
                </Box>
                <Box>
                  <TextField
                    {...register("message")}
                    translation-key="common_enter_your_message"
                    placeholder={t("common_enter_your_message")}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: errors.message ? "red" : "#BDC3C9",
                        },
                        "&:hover fieldset": {
                          borderColor: errors.message ? "red" : "#BDC3C9",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: errors.message ? "red" : "#BDC3C9",
                        },
                      },
                    }}
                    className={`rounded-[4px] border  bg-white font-normal text-[14px]  font-montserrat tracking-[0] leading-[16px] `}
                  />
                  {errors.message && (
                    <ErrorMessage>{errors.message.message}</ErrorMessage>
                  )}
                </Box>
              </Box>
            </Box>
            <Box className="flex flex-col gap-[5px] justify-center items-center">
              <ReCAPTCHA
                lang="en"
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
                ref={recaptchaRef}
                onChange={onChangeCaptCha}
              />
            </Box>
            <Box>
              <Button
                disabled={mutationSendNeedHelp.isPending}
                isLoading={mutationSendNeedHelp.isPending}
                type="submit"
                btnType={BtnType.Outlined}
                className="!p-[8px_12px] !bg-[#1F61A9] rounded-[4px] !text-[#FFFFFF] font-montserrat font-medium leading-4  cursor-pointer popup-need-send-button"
                translation-key="common_send_your_message"
              >
                {t("common_send_your_message")}
              </Button>
            </Box>

            <Box>
              <Typography
                component="span"
                className="!font-[Montserrat] !font-medium !text-[14px] !leading-6 !no-underline"
                translation-key="helper_subtitle"
              >
                {t("helper_subtitle")}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {
        !isDisableButton && (
          <Box
            className="flex gap-[8px] cursor-pointer group"
            onClick={() => setOpen(true)}
          >
            {
              !isDisableIcon && (
                <Box>
                  <SupportAgentIcon />
                </Box>
              )
            }
            <Typography
              component="span"
              className="font-[Montserrat] font-medium text-[14px] leading-6 no-underline group-hover:underline group-hover:underline-offset-1"
              translation-key="common_need_help_?"
            >
              {t("common_need_help_?")}
            </Typography>
          </Box>
        )
      }
    </React.Fragment>
  );
});

export default PopupNeed;